import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit,
  EmojiEvents,
  TrendingUp,
  Code,
  Download,
  Star,
  WorkspacePremiumOutlined,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { certificatesAPI, API_BASE_URL } from '../services/api';

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    bio: user?.profile?.bio || '',
  });

  const { data: certificates } = useQuery({
    queryKey: ['certificates'],
    queryFn: certificatesAPI.getMyCertificates,
  });

  const updateProfileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      setEditMode(false);
    },
  });

  const handleSaveProfile = () => {
    updateProfileMutation.mutate(profileData);
  };

  const handleDownloadCertificate = (certificateId: string, certificateName: string) => {
    const token = localStorage.getItem('token');
    const url = `${API_BASE_URL}/certificates/view/${certificateId}?token=${token}`;
    window.open(url, '_blank');
  };

  const getNextLevelProgress = () => {
    const levelThresholds = [0, 100, 250, 500, 1000, 2000, 3500, 5500, 8000, 12000, 17000, 25000];
    const currentPoints = user?.stats?.totalPoints || 0;
    const currentLevel = user?.stats?.level || 1;
    
    if (currentLevel >= levelThresholds.length) return 100;
    
    const currentLevelThreshold = levelThresholds[currentLevel - 1];
    const nextLevelThreshold = levelThresholds[currentLevel];
    const progress = ((currentPoints - currentLevelThreshold) / (nextLevelThreshold - currentLevelThreshold)) * 100;
    
    return Math.min(progress, 100);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Profile Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, gap: 3, textAlign: { xs: 'center', md: 'left' } }}>
              <Avatar
                src={user?.profile?.avatar}
                sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 }, fontSize: { xs: '1.5rem', sm: '2rem' } }}
              >
                {user?.username?.[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                  {user?.profile?.firstName} {user?.profile?.lastName}
                </Typography>
                <Typography variant="h6" color="textSecondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  @{user?.username}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {user?.profile?.bio || 'No bio available'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Chip
                    icon={<EmojiEvents />}
                    label={`Level ${user?.stats?.level ?? 1}`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    icon={<TrendingUp />}
                    label={`${user?.stats?.totalPoints ?? 0} Points`}
                    color="secondary"
                    size="small"
                  />
                  <Chip
                    icon={<Code />}
                    label={`${user?.stats?.solvedProblems ?? 0} Solved`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setEditMode(true)}
                fullWidth={false}
                sx={{ width: { xs: '100%', md: 'auto' }, maxWidth: { xs: 300, md: 'none' } }}
              >
                Edit Profile
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Stats Cards */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Level Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2">Level {user?.stats?.level ?? 1}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={getNextLevelProgress()}
                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2">Level {(user?.stats?.level || 1) + 1}</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {Math.round(getNextLevelProgress())}% to next level
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Streak
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {user?.stats?.streak ?? 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                days in a row
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Points
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                {user?.stats?.weeklyPoints ?? 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                this week
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Badges */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Badges ({user?.badges?.length || 0})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {(user?.badges?.length ?? 0) > 0 ? user!.badges.map((badge, index) => (
                <Card key={index} sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { xs: '1 1 100%', sm: '1 1 200px' }, maxWidth: { xs: '100%', sm: 280 } }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <WorkspacePremiumOutlined sx={{ fontSize: 32, mb: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{badge.name}</Typography>
                    <Typography variant="body2" color="textSecondary">{badge.description}</Typography>
                  </CardContent>
                </Card>
              )) : (
                <Typography variant="body2" color="textSecondary">
                  No badges earned yet. Start solving problems to earn badges!
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Certificates */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Certificates ({certificates?.length || 0})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.isArray(certificates) && certificates.length > 0 ? certificates.map((certificate: any) => (
                <Card key={certificate._id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{certificate.name}</Typography>
                        <Typography variant="body2" color="textSecondary">{certificate.level} Level</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Earned on {new Date(certificate.earnedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button variant="outlined" size="small" startIcon={<Download />}
                        onClick={() => handleDownloadCertificate(certificate._id, certificate.name)}
                        sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}>
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )) : (
                <Typography variant="body2" color="textSecondary">
                  No certificates earned yet. Complete milestone challenges to earn certificates!
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={editMode} onClose={() => setEditMode(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              value={profileData.firstName}
              onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={profileData.lastName}
              onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Bio"
              value={profileData.bio}
              onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMode(false)}>Cancel</Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            disabled={updateProfileMutation.isPending}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;